<template>
  <div :class="['mini-cart__item px-4', { 'first-of-type:mt-4': !isFreeSample }]">
    <div
      class="mini-cart__item-inner flex gap-2 sm:gap-3 justify-between"
      :data-free-sample="isFreeSample"
    >
      <figure
        class="
          mini-cart__item-fig
          shrink-0
          w-16 sm:w-24 md:w-32 lg:w-40
          mb-auto
        ">
        <a
          :href="!isFreeSample ? item.url : '#'"
          :aria-label="item.title"
        >
          <img
            :srcset="getCroppedImageUrl(item.featured_image?.url, [240, 320, 414, 640], minicartImageRatio)"
            :alt="item.featured_image?.title"
            :width="item.featured_image?.width"
            :height="item.featured_image?.height"
            class="block object-cover mini-cart__item-img h-full w-full aspect-square"
            loading="lazy"
          />
        </a>
      </figure>

      <div class="flex-1 min-w-0">
        <a
          v-if="!isFreeSample"
          :href="item.url"
          :aria-label="item.title"
        >
          <h3 class="mini-cart__item-title wrap-break-word">
            {{ item.product_title }}
          </h3>
        </a>
        <h3
          v-else
          class="mini-cart__item-title wrap-break-word"
        >
          {{ item.product_title }}
        </h3>

        <!-- Conditional display for options -->
        <ul
          v-if="isProductWithCustomOptions"
          class="mini-cart__item-options text-xs"
          :class="{
            'pt-2': item.options_with_values.filter(option => option.value !== 'Default Title').length > 0
          }"
        >
          <li
            v-for="option in item.options_with_values.filter(option => option.value !== 'Default Title')"
            :key="option.name"
            class="mini-cart__item-option"
          >
            <span class="mini-cart__item-option-name">{{ option.name }}</span> <span
              class="mini-cart__item-option-value"
            >{{ option.value }}</span>
          </li>
        </ul>

        <!-- Display applied discounts -->
        <ul 
          v-if="appliedDiscounts.length > 0" 
          class="mini-cart__item-discounts list-unstyled text-xs mt-1"
          role="list"
        >
          <li v-for="(discount, index) in appliedDiscounts" :key="index">
            {{ discount }}
          </li>
        </ul>
<!-- Bundle Components -->
<ul
  v-if="cartBundleComponents.length"
  class="mini-cart__bundle-components text-xs mt-2 flex flex-col gap-1"
>
  <li class="font-medium mb-1">{{ bundleIndicatorText }}:</li>
  <li
    v-for="component in cartBundleComponents"
    :key="component.id || component.title"
    class="flex items-center gap-2 pl-2 bg-gray-50 p-1 rounded"
  >
    <img
      v-if="component.image"
      :src="component.image + '&width=40'"
      :alt="component.title"
      width="24"
      height="24"
      class="object-cover rounded shrink-0"
    />
    <div class="flex-1 min-w-0">
      <span class="block font-medium">{{ component.title }}</span>
      <span v-if="component.variant_title && component.variant_title !== 'Default Title'" 
            class="block text-gray-600 text-xs">{{ component.variant_title }}</span>
      <div class="flex items-center gap-2 text-xs text-gray-600">
        <span>{{ quantityLabelText }}: {{ component.quantity }}</span>
        <span v-if="component.price > 0" class="font-medium">
          {{ formatMoney(component.price) }}
        </span>
        <span v-else class="text-green-600 font-medium">{{ freeItemText }}</span>
      </div>
    </div>
  </li>
</ul>
        <!-- Unit price display -->
        <div
          v-if="!isFreeSample"
          class="mini-cart__item-total-price flex mt-1">
          <span class="block mr-1">
            {{ item.discounted_price === 0 ? freeItemText : formatMoney(item.discounted_price) }}
          </span>
          <s
            class="block"
            v-if="item.properties._compare_at_price && item.discounted_price < item.properties._compare_at_price"
          >
            {{ formatMoney(item.properties._compare_at_price) }}
          </s>
          <s
            class="block"
            v-else-if="item.discounted_price < item.price"
          >
            {{ formatMoney(item.price) }}
          </s>
        </div>

        <!-- Quantity Form -->
        <div class="mt-3 sm:mt-4 flex h-fit items-center gap-2 sm:gap-3"
        :class="{ 'justify-center': isFreeSample }">
          <form
            v-if="!isFreeSample"
            class="mini-cart__qty-change relative"
            :class="{ 
              'loading': loading 
            }"
          >
            <IconLoading v-if="loading" class="left-1/2 -translate-x-1/2 w-4 h-4 absolute"/>
            <fieldset
              :disabled="loading"
              class="flex items-center w-fit relative h-6.5 max-w-20"
            >
              <button
                type="button"
                :aria-label="`${decreaseQuantityText} ${item.title}`"
                @click="updateCart(item.quantity - quantityRule.increment)"
                class="cart__decrement_btn inline-flex items-center justify-center h-full w-7 sm:w-8"
                :class="{ 'opacity-0' : loading }"
              >
                <IconMinus />
                <span class="sr-only">{{ decreaseQuantityText }} {{ item.title }}</span>
              </button>
              <label
                class="sr-only"
                :for="`qty-${item.id}`"
              >{{ quantityLabelText }} {{ item.title }}</label>
              <input
                :id="`qty-${item.id}`"
                @blur="updateCart(Number($event.target.value))"
                class="text-center appearance-none h-full bg-background w-7 sm:w-8 mini-cart__increment_qty"
                :class="{ 'opacity-0': loading }"
                type="tel"
                :value="item.quantity"
                :min="quantityRule.min" 
                disabled
              />
              <button
                type="button"
                :disabled="isMaxReached"
                :aria-label="`${increaseQuantityText} ${item.title}`"
                @click="updateCart(item.quantity + quantityRule.increment)"
                class="mini-cart__increment_btn inline-flex items-center justify-center h-7 sm:h-8 w-7 sm:w-8"
                :class="{ 'opacity-0': loading }"
              >
                <IconPlus />
                <span class="sr-only">{{ increaseQuantityText }} {{ item.title }}</span>
              </button>
            </fieldset>
          </form>
          <DeleteButton
            :aria-label="`Delete ${item.title}`"
            :screen-reader-text="removeText"
            :loading="loading"
            @delete="updateCart(0)"
          />
        </div>
        <div v-if="quantityRule.min > 1 && quantityNoteText" class="my-2">
          {{ formattedQuantityNote }}
        </div>

        <!-- Selling Plan Information -->

          <template
            v-for="product in products"
            :key="product.id"
          >
            <div
              v-for="sellingPlanGroup in product.sellingPlanGroups.edges"
              :key="sellingPlanGroup.node.name"
              class="my-2 mini-cart__selling-plan-ui"
            >
              <select
                :id="'selling-plan-' + extractId(product.id)"
                :value="item.selling_plan_allocation?.selling_plan?.id"
                v-model="selectedSellingPlan"
                @change="updateSellingPlan(item, selectedSellingPlan)"
                class="mini-select"
              >
                <option value="">{{ oneTimePurchaseText }}</option>
                <option
                  v-for="option in sellingPlanGroup.node?.sellingPlans?.edges"
                  :key="option.node.name"
                  :value="extractId(option.node.id)"
                >
                  {{ option.node.name }}
                </option>
              </select>
            </div>
          </template>
      </div>

      <!-- Total price section -->
      <div
        v-if="!isFreeSample"
        class="w-auto sm:w-[20%] md:w-[18%] lg:w-[15%] flex flex-col price_section items-end shrink-0">
        <div class="mini-cart__item-price flex flex-1 justify-end">
          <span class="mini-cart__item-total-price text-end">
            <!-- Show the current effective price (discounted or regular) -->
            <b class="block wrap-break-word">
              {{ formatMoney((item.discounted_price < item.price ? item.discounted_price : item.price) * item.quantity) }}
            </b>
            <!-- Only show ONE strikethrough - the highest original price -->
            <s
              class="block text-[0.65rem] sm:text-xs"
              v-if="item.properties._compare_at_price && (item.discounted_price < item.properties._compare_at_price || item.price < item.properties._compare_at_price)"
            >
              {{ formatMoney(item.properties._compare_at_price * item.quantity) }}
            </s>
            <s
              class="block text-[0.65rem] sm:text-xs"
              v-else-if="item.discounted_price < item.price"
            >
              {{ formatMoney(item.price * item.quantity) }}
            </s>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import formatMoney from '../helpers/format-money.js'
import { getCroppedImageUrl } from '../helpers/get-cropped-image-url.js'
import IconMinus from './mini-cart--icon-minus.vue'
import IconPlus from './mini-cart--icon-plus.vue'
import IconLoading from './mini-cart--icon-loading.vue'
import DeleteButton from './mini-cart--delete-button.vue'

const quantityNoteText = window.miniCartCustomSettings?.quantityNoteText || 'Note: This product increases by {increment} with a minimum quantity of {min}';
const freeItemText = window.miniCartCustomSettings?.freeItemText || 'Free';
const decreaseQuantityText = window.miniCartCustomSettings?.decreaseQuantityText || 'Decrease quantity for';
const increaseQuantityText = window.miniCartCustomSettings?.increaseQuantityText || 'Increase quantity for';
const quantityLabelText = window.miniCartCustomSettings?.quantityLabelText || 'Qty';
const removeText = window.miniCartCustomSettings?.language?.removeFromCart || 'Remove';
const oneTimePurchaseText = window.miniCartCustomSettings?.oneTimePurchaseText || 'One Time Purchase';
const minicartImageRatio = window.miniCartCustomSettings?.minicartImageRatio || '4/5';
const bundleIndicatorText = window.miniCartCustomSettings?.language?.bundleIndicator || 'Bundle';

const bundleComponentsMap = inject('bundleComponentsMap', null); 
const cartBundleComponents = computed(() => {
  if (window.miniCartCustomSettings?.showBundleComponents === false) return [];
  const key = props.item?.key;
  if (!key) return [];
  const map = bundleComponentsMap?.value || window.bundleComponentsData;
  const bundleData = map ? map[key] : null;
  return bundleData?.components?.length ? bundleData.components : [];
});

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  updateCartValues: {
    type: Function,
    required: true,
  },
  quantityRule: {
    type: Object,
    default: () => ({ min: 1, increment: 1 })
  },
  currencySymbol: {
    type: String,
    required: true,
    default: window.miniCartCustomSettings.currencySymbol,
  },
  refreshRecommendedProducts: {
    type: Function,
    required: true
  }
});

const formattedQuantityNote = computed(() => {
  if (!quantityNoteText) return '';
  
  return quantityNoteText
    .replace('{increment}', props.quantityRule.increment)
    .replace('{min}', props.quantityRule.min);
});

// Computed property to get applied discounts for this item
const appliedDiscounts = computed(() => {
  const discounts = [];
  
  // Check for line-level discounts
  if (props.item.line_level_discount_allocations && props.item.line_level_discount_allocations.length > 0) {
    props.item.line_level_discount_allocations.forEach(allocation => {
      if (allocation.discount_application && allocation.discount_application.title) {
        discounts.push(allocation.discount_application.title);
      }
    });
  }
  
  // Alternative: Check for discounts in item properties
  if (props.item.discounts && props.item.discounts.length > 0) {
    props.item.discounts.forEach(discount => {
      if (discount.title && !discounts.includes(discount.title)) {
        discounts.push(discount.title);
      }
    });
  }
  
  return discounts;
});

const products = ref([]);

const isProductWithCustomOptions = (item) => {
  return item.options_with_values?.[0]?.name !== 'Title' && item.options_with_values?.[0]?.value !== 'Default Title';
}

const extractId = (gid) => {
  return gid?.split("/").pop() || null;
}
onMounted(async () => {
  await fetchProductSellingPlans();
});

const fetchProductSellingPlans = async () => {
  const endpoint = window.miniCartCustomSettings.storeFrontUrl ? window.miniCartCustomSettings.storeFrontUrl : '';
  const token = window.miniCartCustomSettings.storeFrontToken ? window.miniCartCustomSettings.storeFrontToken : '';
  
  if (endpoint === '' || token === '') return;

  // Convert product_id to GraphQL format
  const productGid = `gid://shopify/Product/${props.item.product_id}`;
  
  const query = `
    query getProduct($id: ID!) {
      product(id: $id) {
        id
        title
        sellingPlanGroups(first: 5) {
          edges {
            node {
              name
              options {
                name
                values
              }
              sellingPlans(first: 10) {
                edges {
                  node {
                    id
                    name
                    description
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const requestBody = {
    query: query,
    variables: {
      id: productGid
    }
  };

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    if (responseData.data.product) {
      products.value = [responseData.data.product];
    }

  } catch (error) {
    console.error('Error fetching Shopify data:', error);
  }
};
</script>

<script>
export default {
  name: "CartItem",
  components: {
    DeleteButton,
    IconPlus
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    updateCartValues: {
      type: Function,
      required: true,
    },
    currencySymbol: {
      type: String,
      required: true,
      default: window.miniCartCustomSettings.currencySymbol,
    },
    refreshRecommendedProducts: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      selectedSellingPlan: this.item.selling_plan_allocation?.selling_plan?.id,
    };
  },
  watch: {
    'item.selling_plan_allocation': {
      immediate: true,
      handler(newAllocation) {
        this.selectedSellingPlan = newAllocation?.selling_plan?.id || '';
      },
    },
  },
  computed: {
    isFreeSample() {
      return this.item.properties?._sample === true || 
             this.item.properties?._sample === 'true';
    },
    isMaxReached() {
      const max = this.quantityRule?.max;
      if (max === null || max === undefined) return false;
      return this.item.quantity >= max;
    }
  },
  methods: {
    updateCart(value) {
      if (this.loading || value === this.item.quantity || value < 0) return;
      this.loading = true;
      fetch(Shopify.routes.root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: this.item.key, quantity: value }),
      })
        .then((response) => response.ok ? response.json() : Promise.reject(response))
        .then((response) => {
          this.updateCartValues(response);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
          if (value === 0) {
            this.refreshRecommendedProducts();
            this.$emit('sample-removed', this.item);
          }
        })
        .catch((response) => {
          this.loading = false;
          response.text().then((text) => {
            console.error("API Error:", text);
            this.$emit("error", `Failed to update the cart: ${text}`);
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateSellingPlan(item, sellingPlanId) {

      if (this.loading || sellingPlanId === item.selling_plan_allocation?.selling_plan?.id) return;

      this.loading = true;

      const requestBody = JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        selling_plan: sellingPlanId || null,
      });

      fetch(Shopify.routes.root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            return Promise.reject(new Error("Failed to update cart."));
          }
        })
        .then((responseData) => {

          this.updateCartValues(responseData);

          this.loading = false;
        })
        .catch((error) => {

          this.loading = false;
          alert(error.message);
        });
    },
    extractId(gid) {
      return gid?.split("/").pop() || null;
    },
  },
};

</script>